import React, { useState, useRef, useEffect } from 'react';
import './ChatbotSidebar.css';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { getApiUrl } from '../utils/api';
import { auth } from '../firebase';
import { authFetch } from '../utils/authFetch';
import { useProject } from '../contexts/ProjectContext';
import {isAdminUserEmail} from '../utils/adminAccess';
import { trackEvent } from '../statsig';

function fixMarkdownTables(text: string): string {
  // Remove <p> tags that wrap table lines
  return text.replace(/<p>([\s\S]*?)<\/p>/g, (match, inner) => {
    if (inner.trim().startsWith('|')) {
      return inner.trim();
    }
    return match;
  });
}

const SUGGESTIONS = [
  'Show me a table of all blocks',
  'What are the video settings?',
  'How many cast members are there?',
];

const WAITING_WORDS = ['Thinking', 'Reading', 'Checking', 'Drafting', 'Analyzing', 'Processing', 'Reviewing', 'Calculating', 'Compiling', 'Generating'];

interface Edit {
  sheet?: string;
  row: number;
  col: number;
  value: string;
  label?: string;
  block?: string;
  expectedValue?: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  pendingEdits?: Edit[];
  confirmed?: boolean;
  snapshot?: Edit[];   // stores original values for undo
  isRetryable?: boolean;
  isStreaming?: boolean;
  requestText?: string;
  documentName?: string;
}

interface ChatbotSidebarProps {
  sheetsUrl?: string | null;
  onLoadGoogleSheets?: () => void;
  canEdit?: boolean;
}

const WaitingWordIndicator: React.FC = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setWordIndex((currentIndex) => (currentIndex + 1) % WAITING_WORDS.length);
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className="waiting-word-indicator" role="status" aria-live="polite" aria-label="Assistant is thinking">
      <span key={WAITING_WORDS[wordIndex]} className="waiting-word">
        {WAITING_WORDS[wordIndex]}...
      </span>
    </div>
  );
};

const AttachmentIcon: React.FC = () => (
  <svg viewBox="0 0 27 27" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m21.4 11.6-8.5 8.5a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.2-8.2" />
  </svg>
);

const DocumentIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6M8 13h8M8 17h6" />
  </svg>
);

const ChatbotSidebar: React.FC<ChatbotSidebarProps> = ({ sheetsUrl, onLoadGoogleSheets, canEdit = true }) => {
  const userEmail = auth.currentUser?.email?.toLowerCase() ?? '';
  const { selectedProject } = useProject();
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Production Assistant online. Ready to help with your project. What scene are we working on today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resizeInput = () => {
    const input = inputRef.current;
    if (!input) return;
    
    const MAX_INPUT_HEIGHT = 120;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, MAX_INPUT_HEIGHT);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    resizeInput();
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      resizeInput();
    }
  }, [isOpen]);

  // ============================================
  // CORE SEND LOGIC — shared by handleSendMessage,
  // handleSuggestionClick, and handleRetry
  // ============================================
  const sendMessage = async (
    text: string,
    history: { role: string; content: string }[],
    source: 'input' | 'suggestion' | 'retry',
    document: File | null = null,
  ) => {
    setIsTyping(true);
    let botMessageId = '';
    let didSucceed = false;

    trackEvent('chatbot_message_sent', {
      source,
      message_length: text.length,
      history_length: history.length,
      has_document: Boolean(document),
    });

    try {
      const payload = {
        input: text,
        sheetUrl: sheetsUrl ?? '',
        projectId: selectedProject?.id ?? '',
        history,
        stream: true,
      };
      const request = document
        ? (() => {
            const formData = new FormData();
            formData.append('input', text);
            formData.append('projectId', selectedProject?.id ?? '');
            formData.append('history', JSON.stringify(history));
            formData.append('stream', 'true');
            formData.append('document', document);
            return { method: 'POST', body: formData };
          })()
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          };

      const response = await authFetch(`${getApiUrl()}/chatbot`, request);

      const contentType = response.headers.get('Content-Type') || '';

      if (response.ok && contentType.includes('text/event-stream')) {
        botMessageId = (Date.now() + 1).toString();
        
        // Add initial message with isStreaming: true
        setMessages((prev) => [...prev, {
          id: botMessageId,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          isStreaming: true,
        }]);

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Readable stream not supported or body is null');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            if (!block.trim()) continue;
            let eventName = '';
            let dataText = '';
            const lines = block.split('\n');
            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventName = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                dataText = line.substring(5).trim();
              }
            }

            if (eventName === 'token') {
              try {
                const parsed = JSON.parse(dataText);
                accumulatedText += parsed.text || '';
                
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId
                      ? { ...msg, text: fixMarkdownTables(accumulatedText.replace(/\\n/g, '\n')) }
                      : msg
                  )
                );
              } catch (e) {
                console.error('Error parsing token data:', e);
              }
            } else if (eventName === 'error') {
              try {
                const parsed = JSON.parse(dataText);
                throw new Error(parsed.message || 'Stream error');
              } catch (e) {
                throw new Error(dataText || 'Stream error');
              }
            } else if (eventName === 'edit_preview') {
              try {
                const parsed = JSON.parse(dataText);
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId
                      ? {
                          ...msg,
                          text: parsed.summary || parsed.message || '',
                          pendingEdits: parsed.edits || [],
                          isStreaming: false,
                        }
                      : msg
                  )
                );
              } catch (e) {
                console.error('Error parsing edit preview data:', e);
              }
            } else if (eventName === 'done') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                )
              );
            }
          }
        }

        // Final remaining buffer
        if (buffer.trim()) {
          let eventName = '';
          let dataText = '';
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventName = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
              dataText = line.substring(5).trim();
            }
          }
          if (eventName === 'token') {
            try {
              const parsed = JSON.parse(dataText);
              accumulatedText += parsed.text || '';
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? { ...msg, text: fixMarkdownTables(accumulatedText.replace(/\\n/g, '\n')) }
                    : msg
                )
              );
            } catch (e) {}
          } else if (eventName === 'edit_preview') {
            try {
              const parsed = JSON.parse(dataText);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? {
                        ...msg,
                        text: parsed.summary || parsed.message || '',
                        pendingEdits: parsed.edits || [],
                        isStreaming: false,
                      }
                    : msg
                )
              );
            } catch (e) {}
          }
        }

        // Finalize streaming state
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? { ...msg, isStreaming: false }
              : msg
          )
        );

        trackEvent('chatbot_response_received', {
          source,
          response_type: 'stream',
        });

      } else {
        // Fallback for standard JSON
        const result = await response.json();

        if (result.status === 'PENDING') {
          // Edit preview — show confirmation bubble
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: result.message,
            sender: 'bot',
            timestamp: new Date(),
            pendingEdits: result.edits,
          }]);

        } else if (result.status === 'OK') {
          let botText: string;
          if (Array.isArray(result.message)) {
            botText = result.message
              .map((edit: Edit) => `Row ${edit.row}, Col ${edit.col}: ${edit.value}`)
              .join('\n');
          } else if (typeof result.message === 'string') {
            botText = fixMarkdownTables(result.message.replace(/\\n/g, '\n'));
          } else {
            botText = String(result.message);
          }
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: botText,
            sender: 'bot',
            timestamp: new Date(),
          }]);

        } else if (result.status === 'ERROR') {
          const errorText = `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: errorText,
            sender: 'bot',
            timestamp: new Date(),
            isRetryable: true,
          }]);

        } else {
          // Fallback for unexpected statuses
          setMessages((prev) => [...prev, {
            id: (Date.now() + 1).toString(),
            text: 'Unexpected response from server.',
            sender: 'bot',
            timestamp: new Date(),
            isRetryable: true,
          }]);
        }

        trackEvent('chatbot_response_received', {
          source,
          response_type: 'json',
          status: result.status ?? 'UNKNOWN',
        });
      }
      didSucceed = response.ok;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      trackEvent('chatbot_response_failed', {
        source,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      let updatedStreamingMessage = false;
      if (botMessageId) {
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === botMessageId);
          if (exists) {
            updatedStreamingMessage = true;
            return prev.map((m) =>
              m.id === botMessageId
                ? {
                    ...m,
                    text: m.text
                      ? `${m.text}\n\n**Error during streaming:**\n\`\`\`\n${detail}\n\`\`\``
                      : `**Error during streaming:**\n\`\`\`\n${detail}\n\`\`\``,
                    isStreaming: false,
                    isRetryable: true,
                  }
                : m
            );
          }
          return prev;
        });
      }

      if (!updatedStreamingMessage) {
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          text: `**Network error. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
          sender: 'bot',
          timestamp: new Date(),
          isRetryable: true,
        }]);
      }
    } finally {
      setIsTyping(false);
    }
    return didSucceed;
  };

  // ============================================
  // SEND MESSAGE — triggered by input
  // ============================================
  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput && !selectedDocument) return;
    const requestText = trimmedInput || 'Scan this Word document and fill matching blank placeholders.';
    const document = selectedDocument;

    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: requestText,
      sender: 'user',
      timestamp: new Date(),
      requestText,
      documentName: document?.name,
    }]);
    setInputValue('');
    setSelectedDocument(null);
    if (documentInputRef.current) documentInputRef.current.value = '';

    const history = messages.map((m) => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    await sendMessage(requestText, history, 'input', document);
  };

  // ============================================
  // SUGGESTION CLICK — triggered by suggestion chips
  // ============================================
  const handleSuggestionClick = async (suggestion: string) => {
    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: suggestion,
      sender: 'user',
      timestamp: new Date(),
    }]);

    await sendMessage(suggestion, [], 'suggestion');
  };

  // ============================================
  // RETRY — resends last user message after error
  // ============================================
  const handleRetry = async () => {
    const lastUserMessage = [...messages].reverse().find((m) => m.sender === 'user');
    if (!lastUserMessage) return;

    // Remove all retryable error messages
    setMessages((prev) => prev.filter((m) => !m.isRetryable));

    const history = messages
      .filter((m) => !m.isRetryable)
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

    const retryDocument = lastUserMessage.documentName === selectedDocument?.name ? selectedDocument : null;
    const didSucceed = await sendMessage(lastUserMessage.requestText ?? lastUserMessage.text, history, 'retry', retryDocument);
    if (retryDocument && didSucceed) {
      setSelectedDocument(null);
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  // ============================================
  // CONFIRM EDIT — applies pending edits to sheet
  // ============================================
  const handleConfirmEdit = async (messageId: string, edits: Edit[]) => {
    setIsTyping(true);

    trackEvent('chatbot_edit_confirmation_requested', {
      edit_count: edits.length,
    });

    try {
      const response = await authFetch(`${getApiUrl()}/chatbot/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl: sheetsUrl ?? '', projectId: selectedProject?.id ?? '', edits }),
      });

      const result = await response.json();

      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
        snapshot: result.status === 'OK' ? result.snapshot : undefined,
      }]);
      if (result.status === 'OK') {
        trackEvent('chatbot_edits_applied', {
          edit_count: edits.length,
        });
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, confirmed: true } : m))
        );
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      trackEvent('chatbot_edits_apply_failed', {
        edit_count: edits.length,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to apply edits. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // ============================================
  // CANCEL EDIT — discards pending edits
  // ============================================
  const handleCancelEdit = (messageId: string) => {
    const editCount = messages.find((message) => message.id === messageId)?.pendingEdits?.length ?? 0;
    trackEvent('chatbot_edits_cancelled', { edit_count: editCount });

    // Mark as cancelled and add a cancel message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, confirmed: false } : m))
    );
    setMessages((prev) => [...prev, {
      id: (Date.now() + 1).toString(),
      text: 'Edit cancelled. No changes were made.',
      sender: 'bot',
      timestamp: new Date(),
    }]);
  };

  const handleScroll = () => {
  const container = messagesContainerRef.current;
  if (!container) return;
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
  setShowScrollButton(distanceFromBottom > 100);
};

  // ============================================
  // UNDO EDIT — restores cells to original values
  // ============================================
  const handleUndo = async (messageId: string, snapshot: Edit[]) => {
    trackEvent('chatbot_undo_requested', { edit_count: snapshot.length });

    // Remove the undo button from this message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, snapshot: undefined } : m))
    );
    setIsTyping(true);

    try {
      const response = await authFetch(`${getApiUrl()}/chatbot/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl: sheetsUrl ?? '', projectId: selectedProject?.id ?? '', snapshot }),
      });

      const result = await response.json();
      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
      }]);
      if (result.status === 'OK') {
        trackEvent('chatbot_undo_completed', { edit_count: snapshot.length });
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      trackEvent('chatbot_undo_failed', {
        edit_count: snapshot.length,
        error_type: err instanceof Error ? err.name : 'unknown',
      });
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to undo. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleClearChat = () => {
    trackEvent('chatbot_cleared', { message_count: messages.length });
    setMessages([{
      id: Date.now().toString(),
      text: 'Production Assistant online. Ready to help with your project. What scene are we working on today?',
      sender: 'bot',
      timestamp: new Date(),
    }]);
    setHasInteracted(false);
  };

  const handleExportChat = () => {
    const exportedAt = new Date();
    const chatExport = {
      exportedAt: exportedAt.toISOString(),
      project: {
        id: selectedProject?.id ?? null,
        sheetsUrl: sheetsUrl ?? null,
      },
      messages,
    };
    const downloadUrl = URL.createObjectURL(
      new Blob([JSON.stringify(chatExport, null, 2)], { type: 'application/json' })
    );
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = `production-hub-chat-${exportedAt.toISOString().replace(/[:.]/g, '-')}.json`;
    downloadLink.click();
    URL.revokeObjectURL(downloadUrl);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isTyping) {
        handleSendMessage();
      }
    }
  };

  if (!isAdminUserEmail(userEmail)) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => {
          const willOpen = !isOpen;
          setIsOpen(willOpen);
          trackEvent(willOpen ? 'chatbot_opened' : 'chatbot_closed');
        }}
        aria-label="Toggle chatbot"
      >
        <svg className="icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <svg className="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Sidebar */}
      <div className={`chatbot-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="chatbot-header">
          <div className="header-content">
            <div className="header-actions">
              <button className="clear-btn" onClick={handleClearChat} aria-label="Clear chat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              </button>
              {userEmail === 'daniel.udassin@gmail.com' && (
                <button className="export-chat-btn" onClick={handleExportChat} aria-label="Export chat as JSON" title="Export chat as JSON">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3v12" />
                    <path d="m7 10 5 5 5-5" />
                    <path d="M5 21h14" />
                  </svg>
                </button>
              )}
            </div>
            <div className="logo-section">
              <div className="terminal-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                </svg>
              </div>
              <div className="header-text">
                <h3>Production Hub</h3>
                <div className="status-line">
                  <span className="status-dot"></span>
                  <span className="status-text">In Production</span>
                </div>
              </div>
            </div>
            <button className="minimize-btn" onClick={() => setIsOpen(false)} aria-label="Close chatbot">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          <div className="scan-line"></div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              <div className="message-avatar">
                {message.sender === 'bot' ? (
                  <svg viewBox="0 0 28 28" fill="currentColor">
                    <path d="m 21.896484,5.0566406 -0.875,1.7832032 c 1.606692,0.78762 2.911492,2.1908405 3.539063,4.0253902 0.62757,1.83455 0.455603,3.744873 -0.332031,5.351563 l 1.783203,0.873047 
                      C 27.026493,15.019794 27.255618,12.56465 26.453125,10.21875 25.65063,7.8728599 23.966527,6.0714206 21.896484,5.0566406 Z M 16.833984,5.9257812 3.2734375,15.6875 c -0.2145341,1.245805 0.048882,2.39094 0.6386719,3.466797 l 3.4394531,-0.02734 1.484375,5.433594 4.0820315,0.0059 -1.414063,-5.439453 
                      9.28125,-0.414062 C 20.21855,15.356069 19.152501,12.26109 16.833984,5.9257812 Z M 20.576172,7.75 19.699219,9.5351562 c 0.917304,0.4496801 1.664005,1.2500708 2.023437,
                      2.3007818 0.359434,1.05071 0.260258,2.141303 -0.189453,3.058593 l 1.785156,0.875 c 0.676826,-1.38065 0.829275,-3.018018 0.294922,-4.580078 C 23.078924,9.627393 21.956828,
                      8.42683 20.576172,7.75 Z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>

              <div className="message-content">
                {message.documentName && (
                  <div className="message-document" title={message.documentName}>
                    <DocumentIcon />
                    <span>{message.documentName}</span>
                  </div>
                )}
                <div className={`message-bubble ${message.pendingEdits ? 'pending' : ''} ${message.isStreaming ? 'streaming' : ''}`}>
                  {message.text ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      remarkRehypeOptions={{ allowDangerousHtml: true }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  ) : message.isStreaming ? (
                    <WaitingWordIndicator />
                  ) : null}

                  {/* Pending edits table */}
                  {message.pendingEdits && message.confirmed === undefined && (
                    <div className="edit-preview">
                      <div className="edit-preview-table-wrapper">
                        <table className="edit-preview-table">
                          <thead>
                            <tr>
                              <th>Target</th>
                              <th>Row</th>
                              <th>Col</th>
                              <th>New Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {message.pendingEdits.map((edit, i) => (
                              <tr key={i}>
                                <td>{edit.block ? `${edit.block} · ${edit.label ?? ''}` : edit.label ?? '—'}</td>
                                <td>{edit.row}</td>
                                <td>{edit.col}</td>
                                <td>{edit.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="edit-confirm-buttons">
                        {canEdit && <button
                          className="confirm-btn"
                          onClick={() => handleConfirmEdit(message.id, message.pendingEdits!)}
                        >
                          ✓ Confirm
                        </button>}
                        <button
                          className="cancel-btn"
                          onClick={() => handleCancelEdit(message.id)}
                        >
                          ✗ Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolved state */}
                  {message.pendingEdits && message.confirmed === true && (
                    <div className="edit-resolved confirmed">✓ Applied</div>
                  )}
                  {message.pendingEdits && message.confirmed === false && (
                    <div className="edit-resolved cancelled">✗ Cancelled</div>
                  )}

                  {/* Undo button — shown after a successful edit apply */}
                  {canEdit && message.snapshot && (
                    <button
                      className="undo-btn"
                      onClick={() => handleUndo(message.id, message.snapshot!)}
                    >
                      ↩ Undo
                    </button>
                  )}

                  {/* Retry button — shown on error messages */}
                  {message.isRetryable && (
                    <button className="retry-btn" onClick={handleRetry}>
                      ↺ Retry
                    </button>
                  )}
                </div>

                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && !messages.some((message) => message.isStreaming) && (
            <div className="message bot typing">
              <div className="message-avatar">
                <svg viewBox="0 0 28 28" fill="currentColor">
                  <path d="m 21.896484,5.0566406 -0.875,1.7832032 c 1.606692,0.78762 2.911492,2.1908405 3.539063,4.0253902 0.62757,1.83455 0.455603,3.744873 -0.332031,5.351563 l 1.783203,0.873047 
                      C 27.026493,15.019794 27.255618,12.56465 26.453125,10.21875 25.65063,7.8728599 23.966527,6.0714206 21.896484,5.0566406 Z M 16.833984,5.9257812 3.2734375,15.6875 c -0.2145341,1.245805 0.048882,2.39094 0.6386719,3.466797 l 3.4394531,-0.02734 1.484375,5.433594 4.0820315,0.0059 -1.414063,-5.439453 
                      9.28125,-0.414062 C 20.21855,15.356069 19.152501,12.26109 16.833984,5.9257812 Z M 20.576172,7.75 19.699219,9.5351562 c 0.917304,0.4496801 1.664005,1.2500708 2.023437,
                      2.3007818 0.359434,1.05071 0.260258,2.141303 -0.189453,3.058593 l 1.785156,0.875 c 0.676826,-1.38065 0.829275,-3.018018 0.294922,-4.580078 C 23.078924,9.627393 21.956828,
                      8.42683 20.576172,7.75 Z" />
                </svg>
              </div>
              <div className="message-content">
                <div className="message-bubble">
                  <WaitingWordIndicator />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          {showScrollButton && (
            <button
              className="scroll-bottom-btn"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>

        {/* Suggestion chips — only before first interaction */}
        {!hasInteracted && (
          <div className="suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="chatbot-input">
          {selectedDocument && (
            <div className="document-selection">
              <span title={selectedDocument.name}><AttachmentIcon />{selectedDocument.name}</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedDocument(null);
                  if (documentInputRef.current) documentInputRef.current.value = '';
                }}
                aria-label="Remove Word document"
              >
                ×
              </button>
            </div>
          )}
          <div className="chatbot-input-wrapper">
            <input
              ref={documentInputRef}
              className="document-input"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(event) => setSelectedDocument(event.target.files?.[0] ?? null)}
              aria-label="Attach Word document"
            />
            <button
              type="button"
              className="document-attach-button"
              onClick={() => documentInputRef.current?.click()}
              disabled={isTyping}
              aria-label="Attach Word document"
              title="Attach a .docx credits document"
            >
              <AttachmentIcon />
            </button>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask about your production..."
              className="message-input"
              rows={1}
            />
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={isTyping || (!inputValue.trim() && !selectedDocument)}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <div className="terminal-footer">
            <span className="footer-text">Enter to send | Shift+Enter for newline</span>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && <div className="chatbot-backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default ChatbotSidebar;
